import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/** @type {Promise<any> | null} */
let cartCache = null;

function getCart() {
  if (!cartCache) {
    cartCache = fetch(Theme.routes.cart_url + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());
  }
  return cartCache;
}

function invalidateCart() {
  cartCache = null;
}

/**
 * @typedef {object} CardBuyButtonRefs
 * @property {HTMLButtonElement} buyButton
 * @property {HTMLElement} quantityWrapper
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} decreaseIcon
 *
 * @extends {Component<CardBuyButtonRefs>}
 */
class CardBuyButtonComponent extends Component {
  /** @type {number} */
  #quantity = 0;

  /** @type {boolean} */
  #busy = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  get variantId() {
    return this.dataset.variantId ?? '';
  }

  get productId() {
    return this.dataset.productId ?? '';
  }

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    this.#initFromCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  async #initFromCart() {
    try {
      const cart = await getCart();
      const item = cart.items?.find(
        /** @param {any} i */ (i) => String(i.variant_id) === this.variantId
      );
      if (item) {
        this.#quantity = item.quantity;
        this.#render();
      }
    } catch {
      // keep default "Comprar" state
    }
  }

  /** @type {EventListener} */
  #onCartUpdate = () => {
    invalidateCart();
    this.#syncWithCart();
  };

  async #syncWithCart() {
    try {
      const cart = await getCart();
      const item = cart.items?.find(
        /** @param {any} i */ (i) => String(i.variant_id) === this.variantId
      );
      this.#quantity = item ? item.quantity : 0;
      this.#render();
    } catch {
      // ignore
    }
  }

  /**
   * Handles the click on the "Comprar" button and adds the item to the cart.
   */
  async addToCart() {
    if (this.#busy || !this.variantId) return;
    this.#setBusy(true);

    const formData = new FormData();
    formData.append('id', this.variantId);
    formData.append('quantity', '1');

    try {
      const cfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...cfg,
        headers: { .../** @type {Record<string, string>} */ (cfg.headers), Accept: 'application/json' },
      });
      const data = await response.json();

      if (data.status) {
        // Shopify returned an error (e.g. out of stock)
        return;
      }

      this.#quantity = 1;
      invalidateCart();
      this.#render();

      this.dispatchEvent(
        new CartAddEvent({}, this.variantId, {
          source: 'product-form-component',
          itemCount: 1,
          productId: this.productId,
        })
      );
    } catch {
      // network error – no-op
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Increases the quantity by 1.
   */
  async increaseQuantity() {
    await this.#changeQuantity(this.#quantity + 1);
  }

  /**
   * Decreases the quantity by 1 (removes if going to 0).
   */
  async decreaseQuantity() {
    await this.#changeQuantity(Math.max(0, this.#quantity - 1));
  }

  /**
   * @param {number} quantity
   */
  async #changeQuantity(quantity) {
    if (this.#busy) return;
    this.#setBusy(true);

    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({ id: this.variantId, quantity }),
        }),
      });
      const cart = await response.json();

      const item = cart.items?.find(
        /** @param {any} i */ (i) => String(i.variant_id) === this.variantId
      );
      this.#quantity = item ? item.quantity : 0;
      invalidateCart();
      this.#render();

      this.dispatchEvent(
        new CartUpdateEvent(cart, this.variantId, {
          source: 'card-buy-button',
          productId: this.productId,
          itemCount: cart.item_count,
        })
      );
    } catch {
      // network error – no-op
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.#busy = busy;
    this.refs.buyButton.setAttribute('aria-busy', String(busy));
  }

  #render() {
    const { buyButton, quantityWrapper, quantityDisplay, decreaseIcon } = this.refs;
    const inCart = this.#quantity > 0;

    buyButton.style.display = inCart ? 'none' : '';
    quantityWrapper.style.display = inCart ? 'flex' : 'none';

    if (inCart) {
      quantityDisplay.textContent = String(this.#quantity);
    }

    // Swap icon: trash when qty = 1, minus otherwise
    if (decreaseIcon) {
      decreaseIcon.innerHTML =
        this.#quantity <= 1
          ? `<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M11.375 3.5H2.625M5.6875 6.125V9.625M8.3125 6.125V9.625M10.5 3.5V11.375C10.5 11.607 10.3069 11.8125 10.0625 11.8125H3.9375C3.69306 11.8125 3.5 11.607 3.5 11.375V3.5M8.75 3.5V2.625C8.75 2.14175 8.37075 1.75 7.875 1.75H6.125C5.62926 1.75 5.25 2.14175 5.25 2.625V3.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>`
          : `<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2.625 7H11.375" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    }
  }
}

if (!customElements.get('card-buy-button')) {
  customElements.define('card-buy-button', CardBuyButtonComponent);
}
